/**
 * Matches backend events.services.eventcode_service.compute_code_range.
 * Rising events use the lower band; resolving pair is +10000 (1xxxx vs 0xxxx).
 */
export function computeCodeRangeFromDigits(
  baseDigit: number,
  subDigit: number
): { min: number; max: number } {
  const x = baseDigit * 1000 + subDigit * 100 + 1;
  return { min: x, max: x + 98 };
}

export function codeBandsFromDigits(
  baseDigit: number,
  subDigit: number
): {
  risingMin: number;
  risingMax: number;
  resolvingMin: number;
  resolvingMax: number;
} {
  const { min, max } = computeCodeRangeFromDigits(baseDigit, subDigit);
  return {
    risingMin: min,
    risingMax: max,
    resolvingMin: risingToResolvingCode(min),
    resolvingMax: risingToResolvingCode(max),
  };
}

export function formatCodeBandRange(min: number, max: number): string {
  return `${min}–${max}`;
}

/** Full rising band for a base (sub digits 0–9): e.g. base 3 → 3001–3998. */
export function computeBaseRisingBandRange(baseDigit: number): { min: number; max: number } {
  return {
    min: computeCodeRangeFromDigits(baseDigit, 0).min,
    max: computeCodeRangeFromDigits(baseDigit, 9).max,
  };
}

/** Unique catalog sub digits for a base, sorted ascending. */
export function uniqueSubDigits(subDigits: number[]): number[] {
  return [...new Set(subDigits.filter((d) => Number.isFinite(d)))].sort((a, b) => a - b);
}

/** Label for one digit or a min–max span (e.g. 5 or 2–5). */
export function formatDigitRangeLabel(min: number, max: number): string {
  if (min === max) return String(min);
  return `${min}–${max}`;
}

/** Sub hundreds-digit span from catalog rows only (not 0–9 unless all exist). */
export function subDigitRangeLabel(subDigits: number[]): string {
  const sorted = uniqueSubDigits(subDigits);
  if (sorted.length === 0) return "–";
  return formatDigitRangeLabel(sorted[0], sorted[sorted.length - 1]);
}

/** Rising/resolving envelope for the sub functions that exist under a base. */
export function codeBandsFromCatalogSubs(
  baseDigit: number,
  subDigits: number[]
): {
  risingMin: number;
  risingMax: number;
  resolvingMin: number;
  resolvingMax: number;
} {
  const sorted = uniqueSubDigits(subDigits);
  if (sorted.length === 0) {
    const rising = computeBaseRisingBandRange(baseDigit);
    return {
      risingMin: rising.min,
      risingMax: rising.max,
      resolvingMin: risingToResolvingCode(rising.min),
      resolvingMax: risingToResolvingCode(rising.max),
    };
  }
  const low = computeCodeRangeFromDigits(baseDigit, sorted[0]);
  const high = computeCodeRangeFromDigits(baseDigit, sorted[sorted.length - 1]);
  return {
    risingMin: low.min,
    risingMax: high.max,
    resolvingMin: risingToResolvingCode(low.min),
    resolvingMax: risingToResolvingCode(high.max),
  };
}

/** Resolving event code for a rising code (e.g. 256 → 10256). */
export function risingToResolvingCode(risingCode: number): number {
  return risingCode + 10000;
}

/** Rising equivalent for lookup and band parsing (identity below 10000). */
export function normalizeToRisingEventCode(code: number): number {
  return isResolvingBandCode(code) ? code - 10000 : code;
}

/** Five digit positions for UI: band (0/1), base, sub, serial tens, serial units. */
export type EventCodeDigitStructure = {
  bandDigit: string;
  baseDigit: number;
  subDigit: number;
  serialTens: number;
  serialUnits: number;
};

export function parseEventCodeDigitStructure(code: number): EventCodeDigitStructure {
  const rising = normalizeToRisingEventCode(code);
  const { baseDigit, subDigit } = parseRisingCodeBandDigits(rising);
  const suffix = rising % 100;
  return {
    bandDigit: isResolvingBandCode(code) ? "1" : "0",
    baseDigit,
    subDigit,
    serialTens: Math.floor(suffix / 10),
    serialUnits: suffix % 10,
  };
}

/** Backend convention: codes in [10000, 20000) are “resolving” events in the event list. */
export function isResolvingBandCode(code: number): boolean {
  return code >= 10000 && code < 20000;
}

/**
 * Human-readable label for the base function’s fixed thousands digit in the event code
 * (e.g. Existing → 0 → 0xxx, Safety → 1 → 1xxx). Not a separate ID.
 */
export function formatBaseThousandsDigit(baseDigit: number): string {
  return `${baseDigit}xxx…`;
}

/**
 * Code pattern for the sub function’s hundreds position within a base band.
 */
export function formatSubHundredsDigit(baseDigit: number, subDigit: number): string {
  return `…${baseDigit}${subDigit}xx…`;
}

/** Thousands and hundreds digit encoded in a rising event code (< 10000). */
export function parseRisingCodeBandDigits(code: number): {
  baseDigit: number;
  subDigit: number;
} {
  return {
    baseDigit: Math.floor(code / 1000),
    subDigit: Math.floor((code % 1000) / 100),
  };
}

/**
 * User-facing message when a code implies a base/sub band that is not in the named catalog overview.
 */
export function getMissingCategoryLookupMessage(
  code: number,
  namedRows: ReadonlyArray<{ baseDigit: number; subDigit: number }>
): string {
  const { baseDigit, subDigit } = parseRisingCodeBandDigits(normalizeToRisingEventCode(code));
  const subBand = formatSubHundredsDigit(baseDigit, subDigit);
  const baseBand = formatBaseThousandsDigit(baseDigit);

  if (namedRows.length === 0) {
    return `${subBand} not in catalog (${baseBand}).`;
  }

  const hasNamedBase = namedRows.some((r) => r.baseDigit === baseDigit);
  const hasNamedSub = namedRows.some(
    (r) => r.baseDigit === baseDigit && r.subDigit === subDigit
  );

  if (!hasNamedBase) {
    return `${baseBand} not in catalog.`;
  }
  if (!hasNamedSub) {
    return `${subBand} not in catalog.`;
  }
  return "Code band not in catalog.";
}
